import { ApolloError } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import z from 'zod';

import { MergeDevProvider } from '@neo.tax/shared/graphql';

import { UseMergeDevParams } from './types';
import { useMergeLinkToken } from './useMergeLinkToken';

export const useMergeDev = (
  { companyId, isParentReady, onError, onSuccess, provider }: UseMergeDevParams,
  mutationFn: (publicToken: any) => Promise<void>,
  mutationError: ApolloError | undefined,
  userId: string,
): { onOpen: () => void } => {
  const [hasOpened, setHasOpened] = useState(false);

  const onMergeTokenLinked = useCallback(
    async (publicToken: string) => {
      await mutationFn(publicToken);
      onSuccess();
    },
    [mutationFn, onSuccess],
  );

  const { fetchLinkToken, isReady, linkToken, open } = useMergeLinkToken({
    userId,
    companyId,
    // This is a hack to get around the fact that the type of ticketingProvider
    // is MergeDevTicketingProvider, but the type of provider is
    // MergeDevProvider, which is a union of all the providers.
    provider: z.enum(MergeDevProvider).parse(provider),
    onSuccess: onMergeTokenLinked,
    onExit: () => setHasOpened(false),
  });

  const onOpen = (): void => {
    fetchLinkToken();
  };

  // When the select providers modal is opened, it adds inert to all other elements.
  // If the merge dev modal has previously been opened, the iframe exists in the dom, and
  // will therefore have the inert attribute added (which means it is completely ignored)
  // This function removes the attribute if the iframe is present.
  // TODO: https://linear.app/neo-tax/issue/NEO-2788/prevent-select-providers-modal-from-adding-inert
  const removeIframeInert = (): void => {
    const mergeIframe = document.getElementById('merge-link-iframe');
    if (!mergeIframe) {
      return;
    }
    mergeIframe.removeAttribute('inert');
  };

  useEffect(() => {
    if (isReady && !!isParentReady && linkToken && !hasOpened) {
      removeIframeInert();
      open();
      setHasOpened(true);
    }
  }, [hasOpened, isParentReady, isReady, linkToken, open, setHasOpened]);

  useEffect(() => {
    if (mutationError) {
      onError();
    }
  }, [mutationError, onError]);

  return { onOpen };
};
